import { supabase } from '../lib/supabase';
import type { Tire, VehicleType } from '../types';
import { stripNonDbFields } from '../utils/strip-non-db-fields';
import { safeApiCall, isSupabaseAvailable } from '../utils/safe-api-call';

// UUID validation regex pattern
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface ProductFilters {
  brand?: string;
  vehicle_type?: VehicleType;
  width?: number;
  profile?: number;
  rim_size?: number;
  min_price?: number;
  max_price?: number;
  search?: string;
}

export const productService = {  /**
   * Get products from the database with optional filtering
   * @param filters Optional filters to apply to the query
   * @param fetchAll Whether to fetch all products (bypassing the 1000 limit) - defaults to false for performance
   * @returns Array of tire data
   */
  async getProducts(filters?: ProductFilters, fetchAll: boolean = false): Promise<Tire[]> {
    // Check if Supabase is initialized properly
    if (!isSupabaseAvailable()) {
      console.error('Supabase client not properly initialized');
      return [];
    }

    try {
      if (fetchAll) {
        // Fetch all products using pagination to bypass Supabase's 1000 row limit
        return await this.getAllProductsPaginated(filters);
      }

      // Standard query (limited to 1000 results by default)
      let query = supabase.from('tires').select('*', { count: 'exact' });
      
      // Apply filters if provided
      if (filters) {
        if (filters.brand) query = query.ilike('brand', filters.brand);
        // For vehicle_type, use contains operator since vehicle_types is an array
        if (filters.vehicle_type) query = query.contains('vehicle_types', [filters.vehicle_type.toLowerCase()]);
        if (filters.width) query = query.eq('size->>width', filters.width.toString());
        if (filters.profile) query = query.eq('size->>profile', filters.profile.toString());
        if (filters.rim_size) query = query.eq('size->>rim_size', filters.rim_size.toString());
        
        if (filters.min_price && filters.max_price) {
          query = query.gte('price', filters.min_price).lte('price', filters.max_price);
        } else if (filters.min_price) {
          query = query.gte('price', filters.min_price);
        } else if (filters.max_price) {
          query = query.lte('price', filters.max_price);
        }
        
        if (filters.search) {
          query = query.or(
            `brand.ilike.%${filters.search}%,model.ilike.%${filters.search}%`
          );
        }
      }
      
      // Execute the query with safe handling
      const result = await safeApiCall(async () => {
        const { data, error } = await query;
        
        if (error) {
          throw new Error(`Failed to fetch products: ${error.message}`);
        }
        
        return data || [];
      });
      
      if (result.error) {
        console.error('Error fetching products:', result.error);
        // Return empty array on error to prevent UI crashes
        return [];
      }
      
      // Convert snake_case properties from DB to camelCase for frontend
      const { convertKeysToCamelCase } = await import('../utils/case-converter');
      const convertedData = result.data?.map(item => {
        // Ensure all items have mandatory fields to prevent UI errors
        const tire = {
          ...item,
          image: item.image || '',
          vehicle_types: item.vehicle_types || [], 
          stock: item.stock || {},
          features: item.features || [],
          specifications: item.specifications || {},
          reviews: item.reviews || []
        };
        return convertKeysToCamelCase(tire) as unknown as Tire;
      }) || [];
      
      return convertedData;
    } catch (error) {
      console.error('Error fetching products:', error);
      return [];
    }
  },

  /**
   * Fetch all products using pagination to bypass Supabase's 1000 row limit
   * @param filters Optional filters to apply to the query
   * @returns Array of all tire data
   */
  async getAllProductsPaginated(filters?: ProductFilters): Promise<Tire[]> {
    const allProducts: Record<string, unknown>[] = [];
    let offset = 0;
    const batchSize = 1000;
    let hasMoreData = true;

    console.log('Fetching all products using pagination to bypass 1000 row limit...');

    try {
      while (hasMoreData) {
        // Build query for this batch
        let query = supabase.from('tires').select('*');
        
        // Apply filters if provided
        if (filters) {
          if (filters.brand) query = query.ilike('brand', filters.brand);
          if (filters.vehicle_type) query = query.contains('vehicle_types', [filters.vehicle_type.toLowerCase()]);
          if (filters.width) query = query.eq('size->>width', filters.width.toString());
          if (filters.profile) query = query.eq('size->>profile', filters.profile.toString());
          if (filters.rim_size) query = query.eq('size->>rim_size', filters.rim_size.toString());
          
          if (filters.min_price && filters.max_price) {
            query = query.gte('price', filters.min_price).lte('price', filters.max_price);
          } else if (filters.min_price) {
            query = query.gte('price', filters.min_price);
          } else if (filters.max_price) {
            query = query.lte('price', filters.max_price);
          }
          
          if (filters.search) {
            query = query.or(
              `brand.ilike.%${filters.search}%,model.ilike.%${filters.search}%`
            );
          }
        }

        // Add pagination
        query = query.range(offset, offset + batchSize - 1);

        // Execute the query
        const result = await safeApiCall(async () => {
          const { data, error } = await query;
          
          if (error) {
            throw new Error(`Failed to fetch products batch: ${error.message}`);
          }
          
          return data || [];
        });

        if (result.error) {
          console.error('Error fetching products batch:', result.error);
          break;
        }

        const batchData = result.data || [];
        
        // If we got fewer items than requested, we've reached the end
        if (batchData.length < batchSize) {
          hasMoreData = false;
        }

        // Add this batch to our collection
        allProducts.push(...batchData);
        
        // Move to next batch
        offset += batchSize;

        console.log(`Fetched batch: ${batchData.length} products (total so far: ${allProducts.length})`);

        // Safety check to prevent infinite loops
        if (allProducts.length > 50000) {
          console.warn('Reached safety limit of 50,000 products. Stopping pagination.');
          break;
        }
      }

      console.log(`Completed pagination. Total products fetched: ${allProducts.length}`);

      // Convert snake_case properties from DB to camelCase for frontend
      const { convertKeysToCamelCase } = await import('../utils/case-converter');
      const convertedData = allProducts.map(item => {
        // Ensure all items have mandatory fields to prevent UI errors
        const tire = {
          ...item,
          image: item.image || '',
          vehicle_types: item.vehicle_types || [], 
          stock: item.stock || {},
          features: item.features || [],
          specifications: item.specifications || {},
          reviews: item.reviews || []
        };
        return convertKeysToCamelCase(tire) as unknown as Tire;
      });
      
      return convertedData;
    } catch (error) {
      console.error('Error in getAllProductsPaginated:', error);
      return [];
    }
  },

  /**
   * Legacy method to maintain backward compatibility
   */
  async getAllProducts(): Promise<Tire[]> {
    try {
      return await this.getProducts();
    } catch (error) {
      console.error('Error in getAllProducts:', error);
      return [];
    }
  },

  /**
   * Get a single product by ID
   */
  async getProduct(id: string): Promise<Tire> {
    if (!UUID_PATTERN.test(id)) {
      throw new Error('Invalid product ID format. Expected UUID.');
    }

    // Check if Supabase is initialized properly
    if (!isSupabaseAvailable()) {
      throw new Error('Database connection unavailable');
    }

    try {
      const result = await safeApiCall(async () => {
        const { data, error } = await supabase
          .from('tires')
          .select('*')
          .eq('id', id)
          .single();
      
        if (error) {
          if (error.message.includes('no rows')) {
            throw new Error(`Product with ID ${id} not found`);
          }
          throw error;
        }
      
        if (!data) {
          throw new Error(`Product with ID ${id} not found`);
        }
        
        return data;
      });
      
      if (result.error) {
        throw result.error;
      }
  
      // Convert snake_case properties from DB to camelCase for frontend
      const { convertKeysToCamelCase } = await import('../utils/case-converter');
      return convertKeysToCamelCase(result.data) as unknown as Tire;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`An unexpected error occurred while fetching product ${id}`);
    }
  },
  
  /**
   * Create a new product
   */
  async createProduct(product: Omit<Tire, 'id' | 'reviews' | 'avgRating'>): Promise<Tire> {
    try {
      const { convertKeysToSnakeCase } = await import('../utils/case-converter');
      // Strip frontend-only fields before DB insert
      const sanitized = stripNonDbFields(product);
      const snakeCaseProduct = convertKeysToSnakeCase(sanitized);
      const { data, error } = await supabase
        .from('tires')
        .insert([snakeCaseProduct])
        .select()
        .single();
        
      if (error) {
        throw new Error(`Failed to create product: ${error.message}`);
      }
      
      if (!data) {
        throw new Error('Failed to create product: No data returned');
      }
      
      const { convertKeysToCamelCase } = await import('../utils/case-converter');
      return convertKeysToCamelCase(data) as unknown as Tire;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred while creating product');
    }
  },
  
  /**
   * Update a product by ID
   */
  async updateProduct(id: string, updates: Partial<Tire>): Promise<Tire> {
    if (!UUID_PATTERN.test(id)) {
      throw new Error('Invalid product ID format. Expected UUID.');
    }
    
    try {
      const { convertKeysToSnakeCase } = await import('../utils/case-converter');
      // Strip frontend-only fields before DB update
      const sanitized = stripNonDbFields(updates);
      const snakeCaseUpdates = convertKeysToSnakeCase(sanitized);
      
      const { data, error } = await supabase
        .from('tires')
        .update(snakeCaseUpdates)
        .eq('id', id)
        .select()
        .single();
        
      if (error) {
        throw new Error(`Failed to update product: ${error.message}`);
      }
      
      if (!data) {
        throw new Error(`Product with ID ${id} not found`);
      }
      
      const { convertKeysToCamelCase } = await import('../utils/case-converter');
      return convertKeysToCamelCase(data) as unknown as Tire;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`An unexpected error occurred while updating product ${id}`);
    }
  },
  
  /**
   * Delete a product by ID
   */
  async deleteProduct(id: string): Promise<void> {
    if (!UUID_PATTERN.test(id)) {
      throw new Error('Invalid product ID format. Expected UUID.');
    }
    
    try {
      const { error } = await supabase
        .from('tires')
        .delete()
        .eq('id', id);
        
      if (error) {
        throw new Error(`Failed to delete product: ${error.message}`);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(`An unexpected error occurred while deleting product ${id}`);
    }
  },
  
  /**
   * Import multiple products
   */
  async importProducts(products: Array<Omit<Tire, 'id' | 'reviews' | 'avgRating'>>): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('tires')
        .insert(products)
        .select('count');
        
      if (error) {
        throw new Error(`Failed to import products: ${error.message}`);
      }
      
      return count || 0;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred while importing products');
    }
  },
  
  /**
   * For backward compatibility - provides filter options
   * Note: This is a simplified version that still works with the
   * old code but doesn't do any real filtering
   */
  async getFilterOptions() {
    try {
      // Just get all tires and extract unique values
      const tires = await this.getAllProducts();
      
      const brands = new Set<string>();
      const vehicleTypes = new Set<string>();
      const widths = new Set<number>();
      const profiles = new Set<number>();
      const rimSizes = new Set<number>();
      
      tires.forEach(tire => {
        if (tire.brand) brands.add(tire.brand);
        
        if (tire.vehicleTypes && Array.isArray(tire.vehicleTypes)) {
          tire.vehicleTypes.forEach(type => vehicleTypes.add(type));
        }
        
        if (tire.size) {
          if (tire.size.width) widths.add(tire.size.width);
          if (tire.size.profile) profiles.add(tire.size.profile);
          if (tire.size.rimSize) rimSizes.add(tire.size.rimSize);
        }
      });

      return {
        brands: Array.from(brands).sort((a, b) => a.localeCompare(b)),
        vehicleTypes: Array.from(vehicleTypes).sort((a, b) => a.localeCompare(b)),
        widths: Array.from(widths).sort((a, b) => a - b),
        profiles: Array.from(profiles).sort((a, b) => a - b),
        rimSizes: Array.from(rimSizes).sort((a, b) => a - b)
      };
    } catch (error) {
      console.error('Error fetching filter options:', error);
      return {
        brands: [],
        vehicleTypes: [],
        widths: [],
        profiles: [],
        rimSizes: []
      };
    }
  },
  
  /**
   * For backward compatibility - get compatible filter options
   */
  async getCompatibleFilterOptions(filters: ProductFilters) {
    // Reuse the same logic as getFilterOptions but applying the filters first
    try {
      const tiresData = await this.getProducts(filters);
      
      const brands = new Set<string>();
      const vehicleTypes = new Set<string>();
      const widths = new Set<number>();
      const profiles = new Set<number>();
      const rimSizes = new Set<number>();
      
      tiresData.forEach((tire: Tire) => {
        if (tire.brand) brands.add(tire.brand);
        
        if (tire.vehicleTypes && Array.isArray(tire.vehicleTypes)) {
          tire.vehicleTypes.forEach((type: string) => vehicleTypes.add(type));
        }
        
        if (tire.size) {
          if (tire.size.width) widths.add(tire.size.width);
          if (tire.size.profile) profiles.add(tire.size.profile);
          if (tire.size.rimSize) rimSizes.add(tire.size.rimSize);
        }
      });

      return {
        brands: Array.from(brands).sort((a, b) => a.localeCompare(b)),
        vehicleTypes: Array.from(vehicleTypes).sort((a, b) => a.localeCompare(b)),
        widths: Array.from(widths).sort((a, b) => a - b),
        profiles: Array.from(profiles).sort((a, b) => a - b),
        rimSizes: Array.from(rimSizes).sort((a, b) => a - b)
      };
    } catch (error) {
      console.error('Error fetching compatible filter options:', error);
      return {
        brands: [],
        vehicleTypes: [],
        widths: [],
        profiles: [],
        rimSizes: []
      };
    }
  },

  /**
   * Bulk update prices for multiple products
   */
  async bulkUpdatePrices(productIds: string[], price: number, salePrice?: number): Promise<void> {
    // Validate input
    if (!productIds || productIds.length === 0) {
      throw new Error('No product IDs provided for bulk update');
    }

    // Validate all IDs are UUIDs
    const invalidIds = productIds.filter(id => !UUID_PATTERN.test(id));
    if (invalidIds.length > 0) {
      throw new Error(`Invalid product ID format. Expected UUIDs: ${invalidIds.join(', ')}`);
    }

    if (price < 0) {
      throw new Error('Price cannot be negative');
    }

    if (salePrice !== undefined && salePrice < 0) {
      throw new Error('Sale price cannot be negative');
    }

    // Check if Supabase is initialized properly
    if (!isSupabaseAvailable()) {
      throw new Error('Database connection unavailable');
    }    try {
      const result = await safeApiCall(async () => {
        // Prepare update data
        const updateData: Record<string, number> = { price };
        if (salePrice !== undefined) {
          updateData.sale_price = salePrice;
        }

        const { data, error } = await supabase
          .from('tires')
          .update(updateData)
          .in('id', productIds)
          .select('id');

        if (error) {
          throw new Error(`Failed to bulk update prices: ${error.message}`);
        }

        if (!data || data.length === 0) {
          throw new Error('No products were updated. Please check if the selected products exist.');
        }

        if (data.length !== productIds.length) {
          console.warn(`Expected to update ${productIds.length} products, but only ${data.length} were updated`);
        }

        return data;
      });

      if (result.error) {
        throw result.error;
      }

      if (result.data) {
        console.log(`Successfully updated ${result.data.length} products`);
      }
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('An unexpected error occurred while updating product prices');
    }
  }
};
